import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { propertyDetails, detailLabels } from "@/data/propertyDetails";
import { X, ArrowRight } from "lucide-react";

export const PropertyDialog = ({ propertyId, open, onOpenChange }) => {
  const { lang } = useLanguage();
  const labels = detailLabels[lang] || detailLabels.en;
  const data = propertyId && propertyDetails[propertyId]
    ? propertyDetails[propertyId][lang] || propertyDetails[propertyId].en
    : null;

  const goToContact = () => {
    onOpenChange(false);
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="property-dialog"
        className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#0A0A0A] text-[#F8F8F8] border border-[#D4AF37]/30 p-0 rounded-none"
      >
        {!data ? (
          <div className="p-10">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl text-[#F8F8F8]">
                {labels.no_detail_title}
              </DialogTitle>
              <DialogDescription className="text-[#A1A1AA] mt-3">
                {labels.no_detail_body}
              </DialogDescription>
            </DialogHeader>
            <button
              data-testid="property-dialog-enquire-empty"
              onClick={goToContact}
              className="mt-8 inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#E6C657] transition-colors px-6 py-3 text-xs uppercase tracking-[0.25em] font-medium"
            >
              {labels.enquire}
              <ArrowRight size={14} className={lang === "ar" ? "rotate-180" : ""} />
            </button>
          </div>
        ) : (
          <div>
            {/* Hero image */}
            <div className="relative h-64 md:h-96 overflow-hidden">
              <img
                src={data.images[0]}
                alt={data.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
              <div className="absolute bottom-6 start-8 end-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                  Brokers Lounge · {data.location}
                </div>
                <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-tight">
                  {data.name}
                </h2>
                <p className="mt-3 text-[#A1A1AA] text-sm md:text-base max-w-2xl">
                  {data.tagline}
                </p>
              </div>
            </div>

            <DialogHeader className="sr-only">
              <DialogTitle>{data.name}</DialogTitle>
              <DialogDescription>{data.tagline}</DialogDescription>
            </DialogHeader>

            <div className="p-8 md:p-10 space-y-10">
              {/* Overview */}
              <section>
                <div className="eyebrow mb-4">{labels.overview}</div>
                <p className="text-[#A1A1AA] leading-relaxed text-base md:text-lg max-w-3xl">
                  {data.description}
                </p>
              </section>

              {/* Specs */}
              <section>
                <div className="eyebrow mb-5">{labels.specs}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
                  {data.specs.map((s, i) => (
                    <div
                      key={i}
                      data-testid={`property-spec-${i}`}
                      className="bg-[#0A0A0A] p-5"
                    >
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                        {s.label}
                      </div>
                      <div className="mt-2 text-[#F8F8F8] font-serif-display text-lg">
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Units */}
              <section>
                <div className="eyebrow mb-5">{labels.units}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
                  {data.units.map((u, i) => (
                    <div key={i} className="bg-[#0A0A0A] p-6">
                      <div className="text-[#D4AF37] font-serif-display text-xl">
                        {u.type}
                      </div>
                      <div className="mt-2 text-sm text-[#A1A1AA]">
                        {u.size}
                      </div>
                      <div className="mt-1 text-xs text-[#71717A]">
                        Parking: {u.parking}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Amenities + Connectivity */}
              <section className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <div className="eyebrow mb-5">{labels.amenities}</div>
                  <ul className="space-y-3">
                    {data.amenities.map((a, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                        <span className="mt-2 w-1.5 h-1.5 bg-[#D4AF37] shrink-0" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="eyebrow mb-5">{labels.connectivity}</div>
                  <ul className="divide-y divide-white/5">
                    {data.connectivity.map((c, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between py-3 text-sm"
                      >
                        <span className="text-[#A1A1AA]">{c.label}</span>
                        <span className="text-[#F8F8F8]">{c.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Gallery */}
              {data.images.length > 1 && (
                <section>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {data.images.slice(1).map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt={`${data.name} ${i + 2}`}
                        className="w-full h-56 md:h-72 object-cover"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Footer: source + CTA */}
              <section className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <p className="text-xs text-[#71717A] max-w-md leading-relaxed">
                  {data.source}
                </p>
                <button
                  data-testid="property-dialog-enquire"
                  onClick={goToContact}
                  className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#E6C657] transition-colors px-6 py-3 text-xs uppercase tracking-[0.25em] font-medium"
                >
                  {labels.enquire}
                  <ArrowRight
                    size={14}
                    className={`${
                      lang === "ar" ? "rotate-180" : ""
                    } group-hover:translate-x-1 transition-transform`}
                  />
                </button>
              </section>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PropertyDialog;
